import React, { useState, useEffect } from "react";
import { Modal, Upload, message, Select, Button, Table } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import ApiService from "../../Components/apiService";
import { BASE_URL } from "../../Components/Constant";
import { DeleteOutlined } from "@ant-design/icons";
import { Popconfirm } from "antd";

const { Option } = Select;
const FIELD_SYNONYMS = {
  Date: ["date", "day", "entry date", "log date"],
  Hours: ["hours", "hour", "hrs", "time", "duration"],
  Notes: ["notes", "note", "details", "detail", "description", "desc", "remarks", "activity", "activities"]
};


const UploadCsvModal = ({ visible, onClose, projects, fetchUserTimesheetsProjects, selectedUser, weekRef }) => {
  const [step, setStep] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);

  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});

  const [previewRows, setPreviewRows] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (visible) {
      setStep(1);
      setSelectedProject(null);
      setParsedData([]);
      setHeaders([]);
      setMapping({});
      setPreviewRows([]);
      console.log('projects are ', projects);
    }
  }, [visible]);

  // Reset mapping & preview when going back from Step 3 → Step 2
  const goBackToUpload = () => {
    setMapping({});
    setPreviewRows([]);
    setStep(2);
  };

  const autoMatchColumn = (csvColumn, targetField) => {
    const col = csvColumn.toLowerCase().trim();
  
    // 🔍 Strong match: exact
    if (col === targetField.toLowerCase()) return true;
  
    // 🔍 Match in synonyms
    return FIELD_SYNONYMS[targetField].some(keyword =>
      col.includes(keyword)
    );
  };
  
  // Reset preview when going back from Step 4 → Step 3
  const goBackToMapping = () => {
    setPreviewRows([]);
    setStep(3);
  };

  const handleUpload = async ({ file }) => {
    if (!selectedProject) {
      message.warning("Please select an assignment first");
      return;
    }

    setUploading(true);

    Papa.parse(file, {
      header: true,
      complete: (result) => {
        const { data, meta } = result;

        if (!data || data.length === 0) {
          message.error("CSV file is empty");
          setUploading(false);
          return;
        }

        setParsedData(data);
        setHeaders(meta.fields);
        const autoMapping = {};
        ["Date", "Hours", "Notes"].forEach((sysField) => {
          const match = meta.fields.find((col) => autoMatchColumn(col, sysField));
          if (match) {
            autoMapping[sysField] = match;
          }
        });

        setMapping(autoMapping);

        setStep(3);
        setUploading(false);
      },
      error: () => {
        message.error("Failed to read CSV");
        setUploading(false);
      },
    });
  };

  const handleMappingChange = (systemField, csvColumn) => {
    setMapping((prev) => ({ ...prev, [systemField]: csvColumn }));
  };

  const handleSubmit = async () => {
    console.log('Parsed data:', parsedData);
    console.log('preview data:', previewRows);
  
    const requiredFields = ["Date", "Hours", "Notes"];
    const missing = requiredFields.filter((f) => !mapping[f]);
  
    if (missing.length > 0) {
      message.warning(`Please map required fields: ${missing.join(", ")}`);
      return;
    }
  
    // --- FUTURE DATE VALIDATION ---
    const today = new Date();
    const futureDates = [];
  
    previewRows.forEach(row => {
      const d = new Date(row[mapping["Date"]]);
      if (!isNaN(d.getTime()) && d > today) {
        futureDates.push(row[mapping["Date"]]);
      }
    });
  
    if (futureDates.length > 0) {
      message.error(
        `Your CSV contains future dates which are not allowed: ${futureDates.join(", ")}`
      );
      return;
    }
  
    // --- ZERO HOUR VALIDATION ---
    const zeroHourRows = previewRows.filter(r => {
      const hours = Number(r["Hours"]);   // DIRECTLY use "Hours"
      return hours === 0 || r["Hours"] === "" || r["Hours"] == null;
    });
    
    console.log('Zero hour rows:', zeroHourRows);
    if (zeroHourRows.length > 0) {
      const modal = Modal.confirm({
        title: "0-Hour Entries Found",
        content: `${zeroHourRows.length} entries have 0 Hours and will be skipped. Do you want to continue?`,
        okText: "Yes",
        cancelText: "No",
    
        async onOk() {
          // Disable both buttons
          modal.update({
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true }
          });
    
          await proceedSubmit();
        },
    
        onCancel() {
          // Disable buttons immediately
          modal.update({
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true }
          });
    
          message.info("Submission cancelled");
        }
      });
    
      return;
    }    
    
    const missingNotesRows = previewRows.filter(r => {
      const hours = Number(r["Hours"]);
      return hours > 0 && (!r["Notes"] || r["Notes"].trim() === "");
    });
    
    if (missingNotesRows.length > 0) {
      message.error(
        `${missingNotesRows.length} entries have Hours > 0 but empty Notes. Please fill Notes before submitting.`
      );
      return;
    }
    
    // If no zero-hour rows, proceed directly
    proceedSubmit();
  };

  const proceedSubmit = async () => {
    try {
      setUploading(true);
  
      // Remove 0-hour rows
      const filteredRows = previewRows.filter(
        r => Number(r.Hours) > 0
      );      
  
      const mappedRows = filteredRows.map(row => ({
        Date: row["Date"] || "",
        Hours: row["Hours"] || "",
        Notes: row["Notes"] || "",
        AssignmentId: selectedProject
      }));
  
      console.log("Mapped Rows:", mappedRows);
  
      const payload = {
        action_info: {
          class_name: "TimesheetController",
          method: "timesheet_csv_upload",
          args: [
            selectedProject,             
            JSON.stringify(mappedRows),  
            "Approved"
          ],
        },
      };
  
      const api = new ApiService(
        `${BASE_URL}/api/classes/execute`,
        { "Content-Type": "application/json" },
        "POST",
        payload
      );
  
      const responseForUploading = await api.makeCall();
  
      if (responseForUploading.error) {
        message.error(responseForUploading.error);
        return;
      }
  
      if (responseForUploading.result?.success === false) {
        message.error(responseForUploading.result.error || "Upload failed");
        return;
      }
  
      message.success(responseForUploading.result?.message || "Timesheet processed");
      fetchUserTimesheetsProjects(selectedUser, weekRef);
      onClose();
  
    } catch (err) {
      console.error(err);
      message.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={750}
      title="Upload Timesheet CSV"
      centered
      zIndex={1001}
    >
      <div style={{ padding: "8px 4px" }}>
  
        {/* STEP 1 — Select Assignment */}
        {step === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ fontWeight: 600 }}>Select Assignment</div>
  
            <Select
              placeholder="Select assignment"
              style={{ width: "100%" }}
              value={selectedProject}
              onChange={setSelectedProject}
              showSearch
              optionFilterProp="children"
            >
              {projects.map((p) => (
                <Option key={p.Assignment} value={p.Assignment}>
                  {p.Name} — {p.AssignmentName}
                </Option>
              ))}
            </Select>
  
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                disabled={!selectedProject}
                onClick={() => setStep(2)}
              >
                Continue
              </Button>
            </div>
          </div>
        )}
  
        {/* STEP 2 — Upload CSV */}
        {step === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{
              padding: 16,
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              background: "#fafafa"
            }}>
              <Upload
                name="file"
                customRequest={handleUpload}
                accept=".csv"
                showUploadList={false}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={uploading}
                  block
                >
                  {uploading ? "Processing..." : "Upload CSV File"}
                </Button>
              </Upload>
            </div>
  
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={() => setStep(1)} disabled={uploading}>
                Back
              </Button>
            </div>
          </div>
        )}
  
        {/* STEP 3 — Mapping */}
        {step === 3 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div style={{ fontWeight: 600 }}>Map CSV Columns</div>
  
            <div style={{
              padding: 12,
              borderRadius: 6,
              border: "1px solid #f0f0f0",
            }}>
              <Table
                dataSource={[
                  { field: "Date", required: true },
                  { field: "Hours", required: true },
                  { field: "Notes", required: true },
                ]}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Field",
                    dataIndex: "field",
                    width: 180,
                    render: (text, rec) => (
                      <>
                        <span style={{ fontWeight: 500 }}>{text}</span>
                        {rec.required && <span style={{ color: "red" }}> *</span>}
                      </>
                    ),
                  },
                  {
                    title: "CSV Column",
                    render: (_, rec) => (
                      <Select
                        style={{ width: "100%" }}
                        value={mapping[rec.field]}
                        placeholder="Select column"
                        onChange={(v) =>
                          setMapping((prev) => ({ ...prev, [rec.field]: v }))
                        }
                      >
                        {headers.map((h) => (
                          <Option key={h} value={h}>{h}</Option>
                        ))}
                      </Select>
                    ),
                  },
                ]}
              />
            </div>
  
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 10
            }}>
              <Button onClick={goBackToUpload}>Back</Button>
  
              <Button
                type="primary"
                onClick={() => {
                  const required = ["Date", "Hours", "Notes"];
                  const missing = required.filter(f => !mapping[f]);
  
                  if (missing.length) {
                    message.warning(`Missing mappings: ${missing.join(", ")}`);
                    return;
                  }
  
                  const rows = parsedData.map(row => {
                    const out = {};
                    Object.entries(mapping).forEach(([sys, col]) => {
                      out[sys] = row[col];
                    });
                    out.AssignmentId = selectedProject;
                    return out;
                  });
  
                  setPreviewRows(rows);
                  setStep(4);
                }}
              >
                Preview
              </Button>
            </div>
          </div>
        )}
  
        {/* STEP 4 — Preview */}
        {step === 4 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ fontWeight: 600 }}>Preview Entries</div>
  
            <div style={{
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              padding: 12,
              maxHeight: 320,
              overflow: "auto",
            }}>
              <Table
                dataSource={previewRows}
                rowKey={(r, idx) => idx}
                pagination={false}
                size="small"
                columns={[
                  ...Object.keys(previewRows[0] || {})
                    .filter((col) => col !== "AssignmentId")
                    .map((col) => ({
                      title: col,
                      dataIndex: col,
                    })),
                    {
                      title: "Action",
                      dataIndex: "action",
                      width: 60,
                      align: "center",
                      render: (_, row, index) => (
                        <Popconfirm
                          title="Are You Sure You Want To Delete this entry?"
                          okText="Yes"
                          cancelText="No"
                          onConfirm={() => {
                            const updated = [...previewRows];
                            updated.splice(index, 1);
                            setPreviewRows(updated);
                          }}
                        >
                          <DeleteOutlined
                            style={{ color: "red", cursor: "pointer", fontSize: 16 }}
                          />
                        </Popconfirm>
                      ),
                    }                    
                ]}
              />

            </div>
  
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 14
            }}>
              <Button onClick={goBackToMapping} disabled={uploading}>Back</Button>
  
              <Button type="primary" onClick={handleSubmit} disabled={uploading} loading={uploading}>
                Confirm & Submit
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
  
};

export default UploadCsvModal;
